import React, { useState, useEffect, useMemo } from 'react';
import { X, Users, AlertTriangle, Check, User } from 'lucide-react';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import { api } from '../../utils/api';
import { toast } from 'react-hot-toast';

// Field configuration for merge
const MERGE_FIELDS = [
  { key: 'FirstName', label: 'customers.firstName', fallback: 'First Name', altKeys: [] },
  { key: 'LastName', label: 'customers.lastName', fallback: 'Last Name', altKeys: [] },
  { key: 'FirstName_Eng', label: 'customers.firstNameEng', fallback: 'First Name (Eng)', altKeys: [] },
  { key: 'LastName_Eng', label: 'customers.lastNameEng', fallback: 'Last Name (Eng)', altKeys: [] },
  { key: 'Title', label: 'customers.title', fallback: 'Title', altKeys: ['PersonTitle'] },
  { key: 'Email', label: 'customers.email', fallback: 'Email', altKeys: ['PersonEmail'] },
  { key: 'Phone', label: 'customers.phone', fallback: 'Phone', altKeys: ['PersonPhone'] },
  { key: 'JobTitle', label: 'customers.jobTitle', fallback: 'Job Title', altKeys: [] },
  { key: 'Department', label: 'customers.department', fallback: 'Department', altKeys: [] },
  { key: 'NickName', label: 'customers.nickName', fallback: 'Nick Name', altKeys: [] },
  { key: 'LineID', label: 'customers.lineId', fallback: 'Line ID', altKeys: [] },
  { key: 'Gender', label: 'customers.gender', fallback: 'Gender', altKeys: [] },
];

const PersonMergeModal = ({ isOpen, onClose, currentCustomer, duplicates, onMergeComplete }) => {
  const { safeT } = useTranslationReady();
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [merging, setMerging] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [fieldSelections, setFieldSelections] = useState({});

  // Initialize with current customer as primary by default
  useEffect(() => {
    if (currentCustomer && !selectedPrimary) {
      setSelectedPrimary(currentCustomer.CustomerID);
    }
  }, [currentCustomer, selectedPrimary]);

  // Get field value from customer, checking alternate keys
  const getFieldValue = (customer, fieldKey, altKeys = []) => {
    if (!customer) return null;
    const keys = [fieldKey, ...altKeys];
    for (const key of keys) {
      if (customer[key] && String(customer[key]).trim() !== '') {
        return String(customer[key]).trim();
      }
    }
    return null;
  };

  // Compute field comparisons whenever customers change
  const fieldComparisons = useMemo(() => {
    if (!currentCustomer || duplicates.length === 0) return [];

    const allCustomers = [currentCustomer, ...duplicates];
    
    return MERGE_FIELDS.map(field => {
      // Get all unique non-empty values across all customers
      const valueMap = new Map(); // value -> [customerIds]
      
      allCustomers.forEach(customer => {
        const val = getFieldValue(customer, field.key, field.altKeys);
        if (val) {
          if (!valueMap.has(val)) {
            valueMap.set(val, []);
          }
          valueMap.get(val).push(customer.CustomerID);
        }
      });

      const uniqueValues = Array.from(valueMap.entries()).map(([value, customerIds]) => ({
        value,
        customerIds,
        customers: customerIds.map(id => allCustomers.find(c => c.CustomerID === id))
      }));

      return {
        ...field,
        uniqueValues,
        hasConflict: uniqueValues.length > 1,
        isEmpty: uniqueValues.length === 0
      };
    });
  }, [currentCustomer, duplicates]);

  // Initialize field selections when primary changes or comparisons update
  useEffect(() => {
    if (!selectedPrimary || fieldComparisons.length === 0) return;

    const allCustomers = [currentCustomer, ...duplicates];
    const primary = allCustomers.find(c => c.CustomerID === selectedPrimary);
    
    const newSelections = {};
    
    fieldComparisons.forEach(field => {
      if (field.isEmpty) {
        newSelections[field.key] = null;
      } else if (field.uniqueValues.length === 1) {
        // Only one value exists, use it
        newSelections[field.key] = field.uniqueValues[0].value;
      } else {
        // Multiple values - prefer primary's value if it has one
        const primaryValue = getFieldValue(primary, field.key, field.altKeys);
        if (primaryValue) {
          newSelections[field.key] = primaryValue;
        } else {
          // Primary has no value, use first available
          newSelections[field.key] = field.uniqueValues[0].value;
        }
      }
    });

    setFieldSelections(newSelections);
  }, [selectedPrimary, fieldComparisons, currentCustomer, duplicates]);

  // Build merged data from selections
  const mergedData = useMemo(() => {
    const data = {};
    MERGE_FIELDS.forEach(field => {
      data[field.key] = fieldSelections[field.key] || null;
    });
    
    // Handle CompanyID separately
    const allCustomers = [currentCustomer, ...duplicates];
    const primary = selectedPrimary ? allCustomers.find(c => c.CustomerID === selectedPrimary) : null;
    if (primary) {
      data.CompanyID = primary.PersonCompanyID || primary.CompanyID || null;
    }
    
    return data;
  }, [fieldSelections, selectedPrimary, currentCustomer, duplicates]);

  const handleFieldSelection = (fieldKey, value) => {
    setFieldSelections(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleMerge = async () => {
    if (!selectedPrimary) {
      toast.error(safeT('customers.selectPrimaryRecord', 'Please select a primary record'));
      return;
    }

    const duplicateId = selectedPrimary === currentCustomer.CustomerID
      ? duplicates[0].CustomerID
      : currentCustomer.CustomerID;

    try {
      setMerging(true);
      const result = await api.post('/customers/merge-persons', {
        primaryCustomerId: selectedPrimary,
        duplicateCustomerId: duplicateId,
        mergedData
      });

      toast.success(safeT('customers.mergeSuccess', 'Person records merged successfully'));
      
      if (onMergeComplete) {
        onMergeComplete(result.customer);
      }
      
      onClose();
    } catch (error) {
      console.error('Merge failed:', error);
      toast.error(error.response?.data?.message || safeT('customers.mergeError', 'Failed to merge person records'));
    } finally {
      setMerging(false);
      setShowConfirmation(false);
    }
  };

  const getDisplayName = (customer) => {
    const parts = [];
    if (customer.FirstName) parts.push(customer.FirstName);
    if (customer.LastName) parts.push(customer.LastName);
    if (parts.length === 0 && customer.FirstName_Eng) parts.push(customer.FirstName_Eng);
    if (parts.length === 0 && customer.LastName_Eng) parts.push(customer.LastName_Eng);
    return parts.join(' ') || safeT('common.unnamed', 'Unnamed');
  };

  const getCustomerBadge = (customer) => {
    if (customer.CustomerID === currentCustomer?.CustomerID) {
      return <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">{safeT('customers.current', 'Current')}</span>;
    }
    return <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded">{customer.CustomerCode}</span>;
  };

  if (!isOpen) return null;

  const allCustomers = [currentCustomer, ...duplicates];
  const conflictCount = fieldComparisons.filter(f => f.hasConflict).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {safeT('customers.mergePerson', 'Merge Person Records')}
            </h2>
            {conflictCount > 0 && (
              <span className="text-sm bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded-full">
                {conflictCount} {safeT('customers.conflictsToResolve', 'conflict(s) to resolve')}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!showConfirmation ? (
            <>
              {/* Warning Message */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                      {safeT('customers.duplicateEmailWarning', 'These person records have the same email address')}
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      {safeT('customers.mergeInstructions', 'Select the primary record, then choose which value to keep for each conflicting field. All related records will be transferred to the primary record.')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Select Primary Record */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {safeT('customers.selectPrimaryRecord', 'Select Primary Record')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allCustomers.map((customer) => (
                    <button
                      key={customer.CustomerID}
                      onClick={() => setSelectedPrimary(customer.CustomerID)}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        selectedPrimary === customer.CustomerID
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <User className="h-5 w-5 text-gray-400" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {getDisplayName(customer)}
                          </span>
                        </div>
                        {selectedPrimary === customer.CustomerID && (
                          <Check className="h-5 w-5 text-blue-500" />
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <div>📧 {getFieldValue(customer, 'Email', ['PersonEmail']) || '-'}</div>
                        <div>📞 {getFieldValue(customer, 'Phone', ['PersonPhone']) || '-'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          {safeT('customers.customerCode', 'Code')}: {customer.CustomerCode}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Field-by-Field Comparison */}
              {selectedPrimary && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {safeT('customers.resolveConflicts', 'Review & Resolve Conflicts')}
                  </h3>
                  <div className="space-y-3">
                    {fieldComparisons.map(field => {
                      if (field.isEmpty) return null; // Skip fields with no values
                      
                      const isConflict = field.hasConflict;
                      
                      return (
                        <div 
                          key={field.key}
                          className={`border rounded-lg p-4 ${
                            isConflict 
                              ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10' 
                              : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {safeT(field.label, field.fallback)}
                            </label>
                            {isConflict && (
                              <span className="text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded">
                                {safeT('customers.conflictingValues', 'Different values')}
                              </span>
                            )}
                          </div>
                          
                          {field.uniqueValues.length === 1 ? (
                            // Single value - just display it
                            <div className="text-gray-900 dark:text-white">
                              {field.uniqueValues[0].value}
                            </div>
                          ) : (
                            // Multiple values - show radio buttons to choose
                            <div className="space-y-2">
                              {field.uniqueValues.map((option, idx) => (
                                <label 
                                  key={idx}
                                  className={`flex items-center space-x-3 p-2 rounded cursor-pointer transition-colors ${
                                    fieldSelections[field.key] === option.value
                                      ? 'bg-blue-100 dark:bg-blue-900/30'
                                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={field.key}
                                    checked={fieldSelections[field.key] === option.value}
                                    onChange={() => handleFieldSelection(field.key, option.value)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="flex-1 text-gray-900 dark:text-white">
                                    {option.value}
                                  </span>
                                  <div className="flex items-center space-x-1">
                                    {option.customers.map(c => (
                                      <span key={c.CustomerID}>
                                        {getCustomerBadge(c)}
                                      </span>
                                    ))}
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Final Merged Data Summary */}
              {selectedPrimary && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {safeT('customers.mergedDataPreview', 'Final Merged Data')}
                  </h3>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      {MERGE_FIELDS.map(field => (
                        <div key={field.key}>
                          <span className="text-gray-500 dark:text-gray-400">{safeT(field.label, field.fallback)}: </span>
                          <span className="text-gray-900 dark:text-white font-medium">
                            {mergedData[field.key] || '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Confirmation Screen */
            <div className="text-center py-8">
              <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {safeT('customers.confirmMerge', 'Confirm Merge')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                {safeT('customers.mergeConfirm', 'Are you sure you want to merge these records? This action cannot be undone. The duplicate record will be permanently deleted.')}
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {safeT('common.back', 'Back')}
                </button>
                <button
                  onClick={handleMerge}
                  disabled={merging}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {merging ? safeT('common.processing', 'Processing...') : safeT('common.confirmMerge', 'Confirm Merge')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showConfirmation && (
          <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {safeT('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => setShowConfirmation(true)}
              disabled={!selectedPrimary}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {safeT('common.merge', 'Merge Records')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonMergeModal;
